// Sidebar.js
import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import {
    Box,
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Collapse,
    Divider,
    useTheme,
    useMediaQuery,
    Menu,
    MenuItem,
    TextField,
    Button,
    styled,
    Badge,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Modal,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
    Logout,
    Person,
    BarChart,
    ExpandMore,
    Brightness4,
    Brightness7,
    Home,
    People,
    Settings,
    History,
    AllInbox,
    RecentActors,
    Monitor,
    QueryStats,
    DynamicFeed,
    Workspaces,
    DarkMode,
    LightMode,
    PermIdentity,
    Bolt,
    Phone,
    NotificationAdd,
    NotificationsActive,
    AccessTime,
    MoreVert,
    ChatBubble,
    QuestionAnswer,
    LibraryBooks,
    Quiz,
    Campaign,
    Ballot,
    WhatsApp,
    Close,
    SupportAgent,
} from '@mui/icons-material';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import { useDarkMode } from '../Context/DarkMode';
 
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import TokenAuth from '../Auth/TokenAuth';
import { global_link, socket_base_url, socketiopath } from './Globallink';
import { Snackbar, Alert } from '@mui/material';
import WebSocketContext from '../Context/WebSocket';
import modalStyle from '../Components/ModalStyle';
import { io as socketIOClient } from 'socket.io-client';
import WhatsAppWebRTCSoftphone from './WhatsAppWebRTCSoftphone'; // same Components folder as Sidebar
import PhoneCallback from '@mui/icons-material/PhoneCallback';
 
const collapsedDrawerWidth = 83;
const expandedDrawerWidth = 195;
const drawerWidth = 240;

// Dedicated webchat socket (same origin/path as Integrations.js)
const WEBCHAT_WS_ORIGIN = (() => {
    try { return new URL(global_link).origin; } catch { return window.location.origin; }
})();
const WEBCHAT_WS_PATH = '/webchat-socket.io/socket.io';
 
const Sidebar = ({ children }) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [activeSubmenu, setActiveSubmenu] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const location = useLocation();
    const currentPath = location.pathname;
    const [phoneNumber, setPhoneNumber] = useState('');
    const { isDarkMode, setIsDarkMode } = useDarkMode();
    const [appAnchorEl, setAppAnchorEl] = useState(null);
    const appMenuOpen = Boolean(appAnchorEl);
    const authToken = process.env.AUTH_TOKEN;
    const [name, setName] = useState('');
    const [level, setLevel] = useState('');
    const [extension, setExtension] = useState('');
    const api = axios.create({ baseURL: global_link });
    const authyToken = localStorage.getItem(authToken);
    const [userLevel, setUserLevel] = useState("");
    const [notificationMessage, setNotificationMessage] = useState('');
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarNavigateTo, setSnackbarNavigateTo] = useState(null); // optional click target
    const [internalChatNotificationCount, setInternalChatNotificationCount] = useState(0);
    const [refreshCount, setRefreshCount] = useState(0);
    const [openExpirySnackbar, setOpenExpirySnackbar] = useState(false);
    const [expiryNotification, setExpiryNotification] = useState('');
    const [state, setState] = useState({ open: false, vertical: 'top', horizontal: 'center' });
    const { vertical, horizontal } = state;
 
    // ── WhatsApp Notification State ──────────────────────────────────────────
    const [whatsAppNotificationCount, setWhatsAppNotificationCount] = useState(0);
    const [whatsAppConfigured, setWhatsAppConfigured] = useState(false);


    // ── Webchat Notification State ───────────────────────────────────────────
    const [webchatNotificationCount, setWebchatNotificationCount] = useState(0);
    const [webchatConfigured, setWebchatConfigured] = useState(false);
    const webchatSocketRef = useRef(null);

    // ── Reminder Notification State ──────────────────────────────────────────
    const [reminderNotificationCount, setReminderNotificationCount] = useState(0);
    const [duePopup, setDuePopup] = useState(null);
 
    const { socket, user_id } = useContext(WebSocketContext);
    const notificationSoundRef = useRef(new Audio('/Tones/notification.mp3'));
    const reminderSoundRef = useRef(new Audio('/Tones/reminder_tone.mp3'));
    const whatsAppCallSoundRef = useRef(new Audio('/Tones/whatsapp_call.mp3'));
    const [autodialerBg, setAutodialerBg] = useState('inherit');
 
    const activeChatUserRef = useRef('');
    useEffect(() => {
        const handler = (e) => { activeChatUserRef.current = e.detail; };
        window.addEventListener('activeChatUser', handler);
        return () => window.removeEventListener('activeChatUser', handler);
    }, []);
 
    const playNotificationSound = () => {
        notificationSoundRef.current.play()
            .then(() => console.log('Sound played successfully!'))
            .catch((error) => console.error('Failed to play sound:', error));
    };

    const playReminderSound = () => {
        reminderSoundRef.current.play()
            .then(() => console.log('Reminder sound played successfully!'))
            .catch((error) => console.error('Failed to play reminder sound:', error));
    };

    const playWhatsAppCallSound = () => {
        try {
            const a = whatsAppCallSoundRef.current;
            a.loop = false; // one-shot alert; softphone loops while ringing
            a.currentTime = 0;
            a.play()
                .then(() => console.log('WhatsApp call sound played'))
                .catch((error) => console.error('Failed to play WhatsApp call sound:', error));
        } catch (error) {
            console.error('WhatsApp call sound error:', error);
        }
    };
 
    const handleAppMenuClose = () => setAppAnchorEl(null);
    const toggleDarkMode = () => setIsDarkMode((prevMode) => !prevMode);
    const handleSidebarToggle = () => setIsSidebarOpen(!isSidebarOpen);
    const toggleSubmenu = (menu) => setActiveSubmenu(activeSubmenu === menu ? '' : menu);
    const handleAppMenuClick = (event) => setAppAnchorEl(event.currentTarget);
    const toggleMenu = (event) => setAnchorEl((prevAnchorEl) => (prevAnchorEl ? null : event.currentTarget));
 
    const [openRight, setRightOpen] = React.useState(true);
    const handleDrawerOpen = () => setRightOpen(true);
    const handleDrawerClose = () => setRightOpen(false);
    const navigate = useNavigate();

    const openWebchatIntegrations = useCallback(() => {
        setWebchatNotificationCount(0);
        setOpenSnackbar(false);
        setSnackbarNavigateTo(null);
        navigate('/integrations', { state: { openTab: 'webchat' } });
    }, [navigate]);
 
    useEffect(() => {
        const authyToken = localStorage.getItem(authToken);
        if (authyToken != null) {
            const decodedToken = jwtDecode(authyToken);
            const userLevel = decodedToken.user_level;
            const expirationTime = decodedToken.exp * 1000;
            const currentTime = Date.now();
            if (currentTime > expirationTime) {
                localStorage.removeItem(authToken);
            } else {
                setUserLevel(userLevel);
            }
        }
    }, []);
 
    const headers = {
        'Authorization': `Bearer ${authyToken}`,
        'Content-Type': 'application/json',
    };

    const getWaHeaders = useCallback(() => ({
        Authorization: `Bearer ${localStorage.getItem(authToken)}`,
        'Content-Type': 'application/json',
    }), [authToken]);
 
    const fetchInternalChatNotificationCount = async () => {
        try {
            const response = await api.get('/chat/notificationcount', {
                headers,
                withCredentials: true,
            });
            setInternalChatNotificationCount(response.data);
        } catch (error) {
            console.error('Error fetching notification count:', error);
            setInternalChatNotificationCount(0);
        }
    };
 
    useEffect(() => {
        fetchInternalChatNotificationCount();
    }, []);

    const fetchReminderNotificationCount = useCallback(async () => {
        try {
            const authyToken = localStorage.getItem(authToken);
            if (!authyToken) return;
            const headers = {
                Authorization: `Bearer ${authyToken}`,
                'Content-Type': 'application/json',
            };
            const [pendingRes, firedRes] = await Promise.all([
                api.get('/reminder/list', { headers, params: { status: 'pending', limit: 1, offset: 0 } }),
                api.get('/reminder/list', { headers, params: { status: 'fired', limit: 1, offset: 0 } }),
            ]);
            const total = (pendingRes.data?.total || 0) + (firedRes.data?.total || 0);
            setReminderNotificationCount(total);
        } catch (error) {
            console.error('Error fetching reminder count:', error);
            setReminderNotificationCount(0);
        }
    }, [api, authToken]);

    useEffect(() => {
        fetchReminderNotificationCount();
    }, [fetchReminderNotificationCount]);

    const completeReminderFromPopup = async (id) => {
        try {
            const authyToken = localStorage.getItem(authToken);
            await api.patch(`/reminder/complete/${id}`, {}, {
                headers: { Authorization: `Bearer ${authyToken}`, 'Content-Type': 'application/json' },
            });
            setReminderNotificationCount(prev => Math.max(0, prev - 1));
        } catch (err) {
            console.error('Error completing reminder:', err);
        }
        setDuePopup(null);
    };

    const callFromPopup = (number) => {
        if (number) window.dispatchEvent(new CustomEvent('copyPhoneNumber', { detail: number }));
        setDuePopup(null);
    };
 
    const checkWhatsAppConfig = useCallback(async () => {
        try {
            const authyToken = localStorage.getItem(authToken);
            if (!authyToken) return;
            
            const decoded = jwtDecode(authyToken);
            const orgId = decoded.organization_id;
            
            const res = await api.get('/whatsapp/getconfigsadmin', { 
                headers: {
                    'Authorization': `Bearer ${authyToken}`,
                    'Content-Type': 'application/json',
                },
                params: { limit: 10 }
            });
            
            const configs = res.data?.data || [];
            const hasConfig = configs.some(c => c.organization_id === orgId);
            setWhatsAppConfigured(hasConfig);
            
            if (hasConfig) {
                try {
                    const unreadRes = await api.get('/whatsapp/unreadcount', { 
                        headers: {
                            'Authorization': `Bearer ${authyToken}`,
                            'Content-Type': 'application/json',
                        },
                        params: { organization_id: orgId }
                    });
                    setWhatsAppNotificationCount(unreadRes.data?.count || 0);
                } catch (err) {
                    console.error('Error fetching WhatsApp unread count:', err);
                    setWhatsAppNotificationCount(0);
                }
            }
        } catch (err) {
            console.error('Error checking WhatsApp config:', err);
            setWhatsAppConfigured(false);
            setWhatsAppNotificationCount(0);
        }
    }, [api, authToken]);
 
    useEffect(() => {
        const handleWhatsAppNotification = (event) => {
            const count = event.detail?.count || 0;
            const configured = event.detail?.configured !== undefined ? event.detail.configured : whatsAppConfigured;
            setWhatsAppNotificationCount(count);
            setWhatsAppConfigured(configured);
            
            if (count > 0) {
                playNotificationSound();
            }
        };
        
        window.addEventListener('whatsAppNotificationUpdate', handleWhatsAppNotification);
        return () => window.removeEventListener('whatsAppNotificationUpdate', handleWhatsAppNotification);
    }, [whatsAppConfigured]);
 
    useEffect(() => {
        checkWhatsAppConfig();
    }, [checkWhatsAppConfig]);
 
    useEffect(() => {
        const interval = setInterval(() => {
            if (whatsAppConfigured) {
                checkWhatsAppConfig();
            }
        }, 30000);
        
        return () => clearInterval(interval);
    }, [whatsAppConfigured, checkWhatsAppConfig]);


    // ─── Webchat Configuration Check (org has active widget) ─────────────────
    const checkWebchatConfig = useCallback(async () => {
        try {
            const token = localStorage.getItem(authToken);
            if (!token) return;

            const decoded = jwtDecode(token);
            const orgId = decoded.organization_id;

            const res = await api.get('/webchat/getconfigsadmin', {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                params: { limit: 100 },
            });

            const configs = res.data?.data || [];
            const hasConfig = configs.some((c) => {
                const sameOrg = String(c.organization_id) === String(orgId);
                const isActive = c.active === 1 || c.active === true || c.active === '1';
                return sameOrg && isActive;
            });
            setWebchatConfigured(hasConfig);
            if (!hasConfig) setWebchatNotificationCount(0);
        } catch (err) {
            // Agents without admin access may get 403 — treat as not configured
            console.error('Error checking Webchat config:', err);
            setWebchatConfigured(false);
            setWebchatNotificationCount(0);
        }
    }, [api, authToken]);

    useEffect(() => {
        checkWebchatConfig();
    }, [checkWebchatConfig]);

    // ─── Webchat: listen for badge clears from Integrations ─────────────────
    useEffect(() => {
        const handleWebchatClear = (event) => {
            if (event.detail?.count != null) {
                setWebchatNotificationCount(event.detail.count);
            } else {
                setWebchatNotificationCount(0);
            }
        };
        window.addEventListener('webchatNotificationUpdate', handleWebchatClear);
        return () => window.removeEventListener('webchatNotificationUpdate', handleWebchatClear);
    }, []);

    // ─── Webchat socket (org-wide visitor messages) ─────────────────────────
    // Only connect when this org has an active webchat config.
    useEffect(() => {
        if (!webchatConfigured) {
            if (webchatSocketRef.current) {
                webchatSocketRef.current.disconnect();
                webchatSocketRef.current = null;
            }
            return;
        }

        const token = localStorage.getItem(authToken);
        if (!token) return;

        const wsocket = socketIOClient(WEBCHAT_WS_ORIGIN, {
            path: WEBCHAT_WS_PATH,
            auth: { token },
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: 10,
            reconnectionDelay: 2000,
        });
        webchatSocketRef.current = wsocket;

        wsocket.on('new_visitor_message', (payload) => {
            const onWebchatPage =
                window.location.pathname.includes('/integrations') &&
                (window.__cs_active_integrations_tab === 5);

            if (!onWebchatPage) {
                setWebchatNotificationCount((prev) => prev + 1);
                const who = payload?.sender || payload?.visitor_name || 'Website visitor';
                setNotificationMessage(`New website chat from ${who}`);
                setSnackbarNavigateTo('webchat');
                setOpenSnackbar(true);
                playNotificationSound();
            }
        });

        return () => {
            wsocket.disconnect();
            webchatSocketRef.current = null;
        };
    }, [authToken, webchatConfigured]);
 
    useEffect(() => {
        const fetchExpiryInfoIfNeeded = async () => {
            if (userLevel === 'supervisor') {
                try {
                    const headers = {
                        Authorization: `Bearer ${authyToken}`,
                        'Content-Type': 'application/json',
                    };
                    const response = await api.get('/user/usercallconfigs', { headers, withCredentials: true });
 
                    const dueDateStr = response.data.dueDate;
                    const currentDateStr = response.data.currentDate;
 
                    if (!dueDateStr || !currentDateStr) {
                        console.warn('Missing dueDate or currentDate in response');
                        return;
                    }
 
                    const dueDate = new Date(dueDateStr);
                    const currentDate = new Date(currentDateStr);
 
                    if (isNaN(dueDate.getTime()) || isNaN(currentDate.getTime())) {
                        console.error('Invalid date format received');
                        return;
                    }
 
                    const normalize = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
                    const normDue = normalize(dueDate);
                    const normCurrent = normalize(currentDate);
                    const dayDiff = Math.floor((normDue.getTime() - normCurrent.getTime()) / (1000 * 60 * 60 * 24));
 
                    const formattedDue = dueDate.toLocaleDateString('en-US', {
                        year: 'numeric', month: 'long', day: 'numeric',
                    });
 
                    let shouldShow = false;
                    let message = "";
 
                    if (dayDiff < 0) {
                        message = (<>Subscription Expired on <strong>{formattedDue}</strong></>);
                        shouldShow = true;
                    } else if (dayDiff <= 7) {
                        message = (<>Subscription Will Expire on <strong>{formattedDue}</strong></>);
                        shouldShow = true;
                    }
 
                    if (shouldShow) {
                        setExpiryNotification(message);
                        setOpenExpirySnackbar(true);
                    }
                } catch (error) {
                    console.error('Error fetching expiry info:', error);
                }
            }
        };
        fetchExpiryInfoIfNeeded();
    }, [userLevel, authyToken]);
 
    useEffect(() => {
        if (!socket) return;
        const handleNewMessage = async (message) => {
            if (message.receiver_id === user_id && message.sender_id !== activeChatUserRef.current) {
                try {
                    const authyToken = localStorage.getItem(authToken);
                    const headers = {
                        Authorization: `Bearer ${authyToken}`,
                        'Content-Type': 'application/json',
                    };
                    fetchInternalChatNotificationCount();
                    const response = await api.get(`/user/getuser/${message.sender_id}`, { headers, withCredentials: true });
                    setNotificationMessage(`New Chat from ${response.data.user_name}`);
                    setSnackbarNavigateTo(null);
                    setOpenSnackbar(true);
                    playNotificationSound();
                } catch (error) {
                    console.error('Failed to fetch user info:', error);
                }
            }
        };
        socket.on('NEW_MESSAGE', handleNewMessage);
        return () => socket.off('NEW_MESSAGE', handleNewMessage);
    }, [socket, user_id]);
 
    useEffect(() => {
        if (!socket) return;
        const handleWhatsApp = async (eventData) => {
            if (eventData.type !== 'new_message') return;
            
            if (!whatsAppConfigured) {
                await checkWhatsAppConfig();
                return;
            }
            
            setWhatsAppNotificationCount(prev => prev + 1);
            
            const msgData = eventData.data;
            const senderName = msgData.sender_name || 'Unknown';
            setNotificationMessage(`New WhatsApp message from ${senderName}`);
            setSnackbarNavigateTo(null);
            setOpenSnackbar(true);
            playNotificationSound();
            
            window.dispatchEvent(new CustomEvent('whatsAppNotificationUpdate', { 
                detail: { 
                    count: whatsAppNotificationCount + 1, 
                    configured: whatsAppConfigured 
                } 
            }));
        };
        
        socket.on('WHATSAPP_RECIEVE_EVENT', handleWhatsApp);
        return () => socket.off('WHATSAPP_RECIEVE_EVENT', handleWhatsApp);
    }, [socket, whatsAppConfigured, whatsAppNotificationCount, checkWhatsAppConfig]);

    useEffect(() => {
        if (!socket) return;

        const handleReminderDue = (payload) => {
            if (payload.userId && String(payload.userId) !== String(user_id)) return;

            setReminderNotificationCount(prev => prev + 1);
            setDuePopup(payload);
            playReminderSound();

            const who = payload.contactName || payload.contactNumber || 'a contact';
            const when = payload.remindAt ? ` at ${new Date(payload.remindAt).toLocaleTimeString()}` : '';
            setNotificationMessage(`Upcoming call: ${who}${when}`);
            setSnackbarNavigateTo(null);
            setOpenSnackbar(true);
        };

        const handleReminderUpdate = () => fetchReminderNotificationCount();

        socket.on('REMINDER_DUE', handleReminderDue);
        socket.on('NEW_REMINDER_UPDATE', handleReminderUpdate);
        return () => {
            socket.off('REMINDER_DUE', handleReminderDue);
            socket.off('NEW_REMINDER_UPDATE', handleReminderUpdate);
        };
    }, [socket, user_id, fetchReminderNotificationCount]);

    // ─── WhatsApp incoming call (works on any page) ───────────────────────────
    useEffect(() => {
        if (!socket || !whatsAppConfigured) return undefined;

        const onCall = (eventData) => {
            if (!eventData) return;

            if (eventData.type === 'call_event') {
                const d = eventData.data || {};
                const event = String(d.event || '').toLowerCase();
                const direction = String(d.direction || '').toUpperCase();

                if (event === 'connect' && direction.includes('USER')) {
                    const from = d.from || d.contact_number || 'customer';
                    const name = d.contact_name || from;
                    setNotificationMessage(`Incoming WhatsApp call from ${name}`);
                    setSnackbarNavigateTo('whatsapp_call');
                    setOpenSnackbar(true);
                    playWhatsAppCallSound();
                }

            }
        };

        socket.on('WHATSAPP_CALL_EVENT', onCall);
        return () => socket.off('WHATSAPP_CALL_EVENT', onCall);
    }, [socket, whatsAppConfigured]);


    useEffect(() => {
        const handleUpdateNotification = () => setRefreshCount((prev) => prev + 1);
        window.addEventListener('updateMainNotificationCount', handleUpdateNotification);
        return () => window.removeEventListener('updateMainNotificationCount', handleUpdateNotification);
    }, []);
 
    useEffect(() => {
        fetchInternalChatNotificationCount();
    }, [refreshCount]);
 
    const navItems = [
        { title: 'Home', icon: (<Tooltip title="Home" placement="right"><Home sx={{ fontSize: '1.2rem' }} /></Tooltip>), path: '/' },
        { title: 'Call Log', icon: (<Tooltip title="Call Log" placement="right"><History sx={{ fontSize: '1.2rem' }} /></Tooltip>), path: '/calls' },
        { title: 'Reminders', icon: (<Tooltip title="Reminders" placement="right"><NotificationsActive sx={{ fontSize: '1.2rem' }} /></Tooltip>), path: '/reminders' },
        { title: 'Dispositions', icon: (<Tooltip title="Dispositions" placement="right"><AllInbox sx={{ fontSize: '1.2rem' }} /></Tooltip>), path: '/dispositions' },
        { title: 'Ticketing', icon: (<Tooltip title="Ticketing" placement="right"><DynamicFeed sx={{ fontSize: '1.2rem' }} /></Tooltip>), path: '/ticketing' },
        { title: 'Contacts', icon: (<Tooltip title="Contacts" placement="right"><RecentActors sx={{ fontSize: '1.2rem' }} /></Tooltip>), path: '/contacts' },
        { title: 'Chat', icon: (<Tooltip title="Chat" placement="right"><QuestionAnswer sx={{ fontSize: '1.2rem' }} /></Tooltip>), path: '/chat' },
        { title: 'Users', icon: (<Tooltip title="Users" placement="right"><People sx={{ fontSize: '1.2rem' }} /></Tooltip>), path: '/users' },
        { title: 'Analytics', icon: (<Tooltip title="Analytics" placement="right"><BarChart sx={{ fontSize: '1.2rem' }} /></Tooltip>), path: '/analytics' },
        { title: 'Integrations', icon: (<Tooltip title="Integrations" placement="right"><Workspaces sx={{ fontSize: '1.2rem' }} /></Tooltip>), path: '/integrations' },
        { title: 'Monitoring', icon: (<Tooltip title="Monitoring" placement="right"><QueryStats sx={{ fontSize: '1.2rem' }} /></Tooltip>), path: '/monitoring' },
        { title: 'Campaigns', icon: (<Tooltip title="Campaigns" placement="right"><Campaign sx={{ fontSize: '1.2rem' }} /></Tooltip>), path: '/campaigns' },
        { title: 'Scripts', icon: (<Tooltip title="Scripts" placement="right"><LibraryBooks sx={{ fontSize: '1.2rem' }} /></Tooltip>), path: '/scripts' },
        { title: 'FAQs', icon: (<Tooltip title="FAQs" placement="right"><Quiz sx={{ fontSize: '1.2rem' }} /></Tooltip>), path: '/faqs' },
        { title: 'Forms', icon: (<Tooltip title="Forms" placement="right"><Ballot sx={{ fontSize: '1.2rem' }} /></Tooltip>), path: '/forms' },
        { title: 'Settings', icon: (<Tooltip title="Settings" placement="right"><Settings sx={{ fontSize: '1.2rem' }} /></Tooltip>), path: '/settings' },
    ];
 
    const filteredNavItems = navItems.filter(item => {
        if (userLevel === 'agent') {
            const agentItems = ['Home', 'Call Log', 'Reminders', 'Dispositions', 'Contacts', 'Interactions', 'Ticketing', 'Chat', 'Integrations', 'Campaigns', 'Scripts', 'FAQs', 'Forms'];
            return agentItems.includes(item.title);
        } else if (userLevel === 'supervisor') {
            const agentItems = ['Home', 'Call Log', 'Reminders', 'Dispositions', 'Contacts', 'Interactions', 'Ticketing', 'Chat', 'Integrations', 'Analytics', 'Monitoring', 'Campaigns', 'Scripts', 'FAQs', 'Forms'];
            return agentItems.includes(item.title);
        } else if (userLevel === 'admin') {
            const agentItems = ['Home', 'Call Log', 'Reminders', 'Dispositions', 'Contacts', 'Interactions', 'Ticketing', 'Chat', 'Integrations', 'Users', 'Analytics', 'Monitoring', 'Campaigns', 'Scripts', 'FAQs', 'Forms'];
            return agentItems.includes(item.title);
        }
        return true;
    });
 
    const dialerTabChange = (tab_no) => {
        if (tab_no === 'campaign_mode') {
            window.dispatchEvent(new CustomEvent('campaignMode', { detail: 'campaignMode' }));
        } else {
            window.dispatchEvent(new CustomEvent('dialerTabChange', { detail: tab_no }));
        }
    };
 
    const updateUserLive = () => {
        const extension = sessionStorage.getItem('extension');
        if (!extension || !socket) return;
        socket.emit('END_SEND_USER_LIVE', { extension });
    };
 
    const handleLogout = () => {
        try {
            if (socket) {
                socket.emit('USER_LOGOUT');
                socket.disconnect();
            }
            if (webchatSocketRef.current) {
                webchatSocketRef.current.disconnect();
                webchatSocketRef.current = null;
            }
            updateUserLive();
 
            const darkMode = localStorage.getItem('darkMode');
            const language = localStorage.getItem('language');
            const themeVal = localStorage.getItem('theme');
 
            localStorage.clear();
            sessionStorage.clear();
 
            if (darkMode !== null) localStorage.setItem('darkMode', darkMode);
            if (language !== null) localStorage.setItem('language', language);
            if (themeVal !== null) localStorage.setItem('theme', themeVal);
 
            if ('indexedDB' in window) {
                try {
                    ['callRecordings', 'callLogs', 'chatHistory', 'conversations'].forEach(dbName => {
                        const request = indexedDB.deleteDatabase(dbName);
                        request.onsuccess = () => console.log(`Database ${dbName} deleted`);
                        request.onerror = (e) => console.warn(`Failed to delete ${dbName}:`, e.target.error);
                        request.onblocked = () => console.warn(`${dbName} deletion blocked`);
                    });
                } catch (dbError) {
                    console.warn('Error clearing IndexedDB:', dbError);
                }
            }
 
            if ('caches' in window) {
                caches.keys().then(cacheNames => {
                    cacheNames.forEach(cacheName => caches.delete(cacheName).catch(() => {}));
                }).catch(() => {});
            }
 
            try {
                document.cookie.split(';').forEach(cookie => {
                    const [name] = cookie.trim().split('=');
                    if (name) {
                        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
                        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
                        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`;
                    }
                });
            } catch (cookieError) {
                console.warn('Error clearing cookies:', cookieError);
            }
 
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.getRegistrations()
                    .then(registrations => registrations.forEach(r => r.unregister().catch(() => {})))
                    .catch(() => {});
            }
 
            setTimeout(() => {
                window.location.href = '/login';
            }, 100);
 
        } catch (error) {
            console.error('Error during logout:', error);
            localStorage.clear();
            sessionStorage.clear();
            window.location.href = '/login';
        }
    };
 
    const convertToCamelCase = (text) => {
        return text
            .toLowerCase()
            .split(/[^a-zA-Z0-9]+/)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };
 
    const userInfo = () => {
        const authyToken = localStorage.getItem(authToken);
        const headers = {
            'Authorization': `Bearer ${authyToken}`,
            'Content-Type': 'application/json',
        };
        api.get('/user/user', { headers, withCredentials: true })
            .then(response => {
                setName(convertToCamelCase(response.data.user_name));
                setLevel(convertToCamelCase(response.data.user_level));
                setExtension(response.data.user_extension);
            })
            .catch(error => console.error(error));
    };
 
    useEffect(() => {
        userInfo();
    }, []);
 
    useEffect(() => {
        const handleNotifyAutoDialer = (event) => setAutodialerBg(event.detail);
        window.addEventListener('notifyAutoDialer', handleNotifyAutoDialer);
        return () => window.removeEventListener('notifyAutoDialer', handleNotifyAutoDialer);
    }, []);

    const openWhatsAppIntegrations = useCallback(() => {
        setOpenSnackbar(false);
        setSnackbarNavigateTo(null);
        navigate('/integrations', { state: { openTab: 'whatsapp' } });
    }, [navigate]);

    const handleSnackbarClick = () => {
        if (snackbarNavigateTo === 'webchat') {
            openWebchatIntegrations();
        } else if (snackbarNavigateTo === 'whatsapp_call' || snackbarNavigateTo === 'whatsapp') {
            openWhatsAppIntegrations();
        }
    };
 
    return (
        <Box sx={{ display: 'flex' }}>
            <TokenAuth/>
            <AppBar
                position="fixed"
                sx={{
                    width: isSmallScreen ? '100%' : `calc(100% - ${isSidebarOpen ? expandedDrawerWidth : collapsedDrawerWidth}px)`,
                    ml: isSmallScreen ? 0 : `${isSidebarOpen ? expandedDrawerWidth : collapsedDrawerWidth}px`,
                    bgcolor: 'background.appbar',
                    color: 'text.primary',
                    boxShadow: 'none',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Toolbar variant="dense">
                    <IconButton 
                        edge="start" 
                        onClick={handleSidebarToggle}
                        sx={{ color: 'text.primary' }}
                    >
                        <DensityMediumIcon sx={{ fontSize: '1.2rem' }} />
                    </IconButton>
                    <Box sx={{ flexGrow: 1 }} />
                    <Typography sx={{ marginRight: '11px', fontSize: '0.9rem' }}>
                        {name} | {extension}
                    </Typography>

                    <IconButton
                        component={Link}
                        to="/reminders"
                        onClick={() => setReminderNotificationCount(0)}
                        size="small"
                        sx={{ color: 'inherit' }}
                    >
                        <Badge
                            badgeContent={reminderNotificationCount > 0 ? reminderNotificationCount : null}
                            color="warning"
                        >
                            <NotificationsActive sx={{ fontSize: '1.2rem' }} />
                        </Badge>
                    </IconButton>

                    {whatsAppConfigured && (
                        <IconButton 
                            component={Link} 
                            to="/integrations" 
                            size="small" 
                            sx={{ color: 'inherit', position: 'relative' }}
                        >
                            <Badge 
                                badgeContent={whatsAppNotificationCount > 0 ? whatsAppNotificationCount : null} 
                                color="success"
                                sx={{
                                    '& .MuiBadge-badge': {
                                        backgroundColor: '#25D366',
                                        color: 'white',
                                    }
                                }}
                            >
                                <WhatsApp sx={{ fontSize: '1.2rem' }} />
                            </Badge>
                        </IconButton>
                    )}

                    {/* ─── Website Webchat Notification (only if org has webchat) ── */}
                    {webchatConfigured && (
                        <Tooltip title="Website chats">
                            <IconButton
                                onClick={openWebchatIntegrations}
                                size="small"
                                sx={{ color: 'inherit' }}
                            >
                                <Badge
                                    badgeContent={webchatNotificationCount > 0 ? webchatNotificationCount : null}
                                    color="info"
                                >
                                    <SupportAgent sx={{ fontSize: '1.2rem' }} />
                                </Badge>
                            </IconButton>
                        </Tooltip>
                    )}
                    
                    <IconButton onClick={() => dialerTabChange(0)} size="small" sx={{ color: 'inherit' }}>
                        <Phone sx={{ fontSize: '1.2rem' }} />
                    </IconButton>
                    
                    <IconButton 
                        onClick={() => fetchInternalChatNotificationCount()} 
                        component={Link} 
                        to="/chat" 
                        size="small" 
                        sx={{ color: 'inherit' }}
                    >
                        <Badge 
                            badgeContent={internalChatNotificationCount > 0 ? internalChatNotificationCount : null} 
                            color="error"
                        >
                            <ChatBubble sx={{ fontSize: '1.2rem' }} />
                        </Badge>
                    </IconButton>
                    
                    <IconButton onClick={() => dialerTabChange('campaign_mode')} size="small" sx={{ color: 'inherit' }}>
                        <Bolt sx={{ fontSize: '1.2rem', color: autodialerBg }} />
                    </IconButton>
                    
                    <IconButton onClick={() => dialerTabChange(3)} size="small" sx={{ color: 'inherit' }}>
                        <Settings sx={{ fontSize: '1.2rem' }} />
                    </IconButton>
                    
                    <IconButton onClick={handleAppMenuClick} aria-controls={appMenuOpen ? "app-menu" : undefined} aria-expanded={appMenuOpen ? "true" : undefined} aria-haspopup="true" size="small" sx={{ color: 'inherit' }}>
                        <Person sx={{ fontSize: '1.2rem', color: 'inherit' }} />
                    </IconButton>
                    
                    <IconButton onClick={toggleDarkMode} size="small" sx={{ color: 'inherit' }}>
                        {isDarkMode ? <LightMode sx={{ fontSize: '1.2rem' }} /> : <DarkMode sx={{ fontSize: '1.2rem' }} />}
                    </IconButton>

                    <Divider />
                </Toolbar>
            </AppBar>

            <Drawer
                variant={isSmallScreen ? 'temporary' : 'permanent'}
                open={isSidebarOpen}
                onClose={handleSidebarToggle}
                sx={{
                    width: isSidebarOpen ? expandedDrawerWidth : collapsedDrawerWidth,
                    '& .MuiDrawer-paper': {
                        width: isSidebarOpen ? expandedDrawerWidth : collapsedDrawerWidth,
                        bgcolor: 'background.sidebar',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        overflowX: 'hidden',
                        transition: theme.transitions.create('width'),
                    },
                }}
            >
                <Toolbar sx={{ minHeight: '48px !important', cursor: 'pointer' }}>
                    <Typography 
                        variant="subtitle2" 
                        sx={{ 
                            textDecoration: "none !important",
                            color: "inherit", 
                            flexGrow: 1, 
                            textAlign: 'center', 
                            fontWeight: 550, 
                            fontSize: '1.4rem'
                        }}   
                        component={Link}
                        to="/" 
                    >
                        {isSidebarOpen && 'Call Suite CX'}
                    </Typography>
                </Toolbar>

                <Menu
                    id="app-menu"
                    anchorEl={appAnchorEl}
                    open={appMenuOpen}
                    onClose={handleAppMenuClose}
                    MenuListProps={{
                        "aria-labelledby": "app-button",
                    }}
                    slotProps={{
                        paper: {
                            sx: {
                                width: 165,
                                bgcolor: 'background.appbar',
                                boxShadow: 'none', 
                                mt: 1.5, 
                                marginLeft: '10px !important',
                                border: `1px solid rgba(207, 211, 212, 0.2)`,
                            }
                        }
                    }}
                >
                    <Typography 
                        variant="body2" 
                        component="div" 
                        sx={{
                            paddingBottom: '5px', 
                            textAlign: 'center'
                        }}
                    >
                        <Typography 
                            variant="subtitle1" 
                            component="div" 
                            sx={{ fontSize: '17px', padding: '0px!important' }}
                        >
                            {name}
                        </Typography>
                        ( {level} - {extension} )
                    </Typography>
                    <Divider sx={{ marginBottom: '5px', marginTop: '5px' }}/>
                    <MenuItem component={Link} to="/profile">
                        <PermIdentity/> <Typography sx={{ ml: 1 }}> Profile</Typography>
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>
                        <Logout/> <Typography sx={{ ml: 1 }}> Logout</Typography>
                    </MenuItem>
                </Menu>
                
                <Divider />

                <List sx={{ px: 2, marginTop: '14px' }}>
                    {filteredNavItems.map((item, index) => (
                        <Box key={index}>
                            <ListItem
                                button
                                onClick={() => item.submenu ? toggleSubmenu(item.title) : navigate(item.path)}
                                sx={{
                                    borderRadius: '8px',
                                    mb: 0.4,
                                    bgcolor: currentPath === item.path ? 'action.hover' : 'inherit',
                                    '&:hover': {
                                        bgcolor: 'action.hover',
                                        cursor: 'pointer'
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ color: 'text.primary', minWidth: 35 }}>
                                    {item.icon}
                                </ListItemIcon>
                                {isSidebarOpen && (
                                    <>
                                        <ListItemText
                                            primary={item.title}
                                            primaryTypographyProps={{
                                                variant: 'body2',
                                                sx: { fontWeight: 500, fontSize: '1.0rem' }
                                            }}
                                        />
                                        {item.submenu && (
                                            <ExpandMore
                                                sx={{
                                                    fontSize: '1.1rem',
                                                    transform: activeSubmenu === item.title ? 'rotate(180deg)' : 'none',
                                                    transition: '0.2s',
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                            </ListItem>
                            {isSidebarOpen && item.submenu && activeSubmenu === item.title && (
                                <Collapse in={true} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {item.submenu.map((subItem, subIndex) => (
                                            <ListItem
                                                button
                                                key={subIndex}
                                                onClick={() => navigate(subItem.path)}
                                                sx={{
                                                    pl: 4,
                                                    borderRadius: '8px',
                                                    mb: 0.5,
                                                    bgcolor: currentPath === item.path ? 'action.hover' : 'inherit',
                                                    '&:hover': {
                                                        bgcolor: 'action.hover',
                                                        cursor: 'pointer'
                                                    },
                                                }}
                                            >
                                                <ListItemText
                                                    primary={subItem.title}
                                                    primaryTypographyProps={{
                                                        variant: 'body2',
                                                        sx: { fontWeight: 400, fontSize: '0.9rem' }
                                                    }}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Collapse>
                            )}
                        </Box>
                    ))}
                </List>
            </Drawer>

            <Snackbar 
                open={openSnackbar} 
                anchorOrigin={{ vertical, horizontal }} 
                autoHideDuration={snackbarNavigateTo === 'webchat' ? 8000 : null} 
                onClose={() => { setOpenSnackbar(false); setSnackbarNavigateTo(null); }}
            >
                <Alert 
                    onClose={() => { setOpenSnackbar(false); setSnackbarNavigateTo(null); }} 
                    severity="success" 
                    sx={{
                        width: '100%',
                        cursor: snackbarNavigateTo ? 'pointer' : 'default',
                    }}
                    onClick={handleSnackbarClick}
                >
                    {notificationMessage}
                    {snackbarNavigateTo === 'webchat' && (
                        <Typography component="span" sx={{ display: 'block', fontSize: '0.75rem', mt: 0.5, opacity: 0.85 }}>
                            Click to open Webchat
                        </Typography>
                    )}
                    {(snackbarNavigateTo === 'whatsapp_call' || snackbarNavigateTo === 'whatsapp') && (
                        <Typography component="span" sx={{ display: 'block', fontSize: '0.75rem', mt: 0.5, opacity: 0.85 }}>
                            Click to open WhatsApp / answer call
                        </Typography>
                    )}
                </Alert>
            </Snackbar>

            <Snackbar
                open={openExpirySnackbar}
                anchorOrigin={{ vertical, horizontal }}
                autoHideDuration={null}
            >
                <Alert
                    severity="error"
                    sx={{
                        width: '100%',
                        fontWeight: 'bold',
                        animation: 'pulse 2s infinite',
                        '@keyframes pulse': {
                            '0%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.7)' },
                            '70%': { boxShadow: '0 0 0 10px rgba(244, 67, 54, 0)' },
                            '100%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)' },
                        }
                    }}
                >
                    {expiryNotification}
                </Alert>
            </Snackbar>

            <Modal
                open={Boolean(duePopup)}
                onClose={(event, reason) => {
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                    setDuePopup(null);
                }}
                aria-labelledby="reminder-modal-title"
                aria-describedby="reminder-modal-description"
            >
                <Box sx={{ ...modalStyle, maxWidth: '500px' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <h3 id="reminder-modal-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <AccessTime color="warning" /> Upcoming Call
                        </h3>
                        <Box display="flex" justifyContent="right">
                            <Close onClick={() => { setDuePopup(null); setOpenSnackbar(false); }} sx={{ cursor: 'pointer' }} />
                        </Box>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        {duePopup?.contactName && (
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                {duePopup.contactName}
                            </Typography>
                        )}
                        {duePopup?.contactNumber && (
                            <Typography variant="body1" sx={{ mt: 1 }}>
                                {duePopup.contactNumber}
                            </Typography>
                        )}
                        {duePopup?.remindAt && (
                            <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                                Scheduled for {new Date(duePopup.remindAt).toLocaleTimeString()}
                            </Typography>
                        )}
                        {duePopup?.note && (
                            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                                {duePopup.note}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                        <Button onClick={() => { setDuePopup(null); setOpenSnackbar(false); }}>
                            Dismiss
                        </Button>
                        {duePopup?.reminderId && (
                            <Button 
                                color="success" 
                                onClick={() => { completeReminderFromPopup(duePopup.reminderId); setOpenSnackbar(false); }}
                            >
                                Mark done
                            </Button>
                        )}
                        {duePopup?.contactNumber && (
                            <Button 
                                variant="contained" 
                                startIcon={<Phone />} 
                                onClick={() => { callFromPopup(duePopup.contactNumber); setOpenSnackbar(false); }}
                            >
                                Call now
                            </Button>
                        )}
                    </Box>
                </Box>
            </Modal>

            {/* Global WhatsApp WebRTC softphone — rings even when not on Integrations */}
            {whatsAppConfigured && (
                <WhatsAppWebRTCSoftphone
                    getHeaders={getWaHeaders}
                    socket={socket}
                    activeContactNumber={null}
                    onNotify={({ severity, message }) => {
                        setNotificationMessage(message);
                        setSnackbarNavigateTo(severity === 'error' ? null : 'whatsapp');
                        setOpenSnackbar(true);
                        if (severity !== 'error') playNotificationSound();
                    }}
                />
            )}



        </Box>
    );
};

export default Sidebar;